import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Gift, Copy, Check, Users, Award, Loader2, AlertCircle } from "lucide-react";

const gbp = (pence) => `£${((pence || 0) / 100).toFixed(2)}`;

export default function DriverReferralPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [copiedField, setCopiedField] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/driver/referral");
      setData(res.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load referral info");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 1800);
    } catch {
      /* noop */
    }
  };

  if (loading) {
    return (
      <section className="mb-12" data-testid="driver-referral-panel">
        <p className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading referrals…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mb-12" data-testid="driver-referral-panel">
        <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="driver-referral-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      </section>
    );
  }

  const availableCredit = (data?.credit_pence || 0) - (data?.credit_used_pence || 0);
  const bonusGbp = ((data?.bonus_per_referral_pence || 0) / 100).toFixed(0);

  return (
    <section className="mb-12" data-testid="driver-referral-panel">
      <div className="bg-gradient-to-br from-[#1E4D55] to-[#0F3138] text-white p-8 border border-[#0A0A0A]/10">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="shrink-0 h-12 w-12 bg-white/10 border border-white/20 flex items-center justify-center">
            <Gift size={22} />
          </div>
          <div className="flex-1 min-w-[260px]">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/70">Refer a driver · Earn £{bonusGbp}</p>
            <h2 className="font-display mt-2 text-2xl font-bold tracking-tight">Invite a Luton van driver — you both get £{bonusGbp} off Pro</h2>
            <p className="mt-2 text-sm text-white/80 max-w-xl">
              Share your code. When they finish verification, you both receive £{bonusGbp} credit toward Raanah Pro membership.
            </p>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <div className="bg-white/5 border border-white/15 p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/60">Your code</p>
            <div className="mt-2 flex items-center gap-2">
              <span data-testid="referral-code-value" className="font-mono text-xl font-bold tracking-wider">{data?.referral_code}</span>
              <button
                onClick={() => copy(data?.referral_code, "code")}
                data-testid="referral-code-copy"
                className="ml-auto inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-2.5 py-1.5 text-xs font-semibold transition-colors"
              >
                {copiedField === "code" ? <Check size={12} /> : <Copy size={12} />}
                {copiedField === "code" ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
          <div className="bg-white/5 border border-white/15 p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/60">Share link</p>
            <div className="mt-2 flex items-center gap-2">
              <span data-testid="referral-share-url" className="truncate text-xs font-mono text-white/90">{data?.share_url}</span>
              <button
                onClick={() => copy(data?.share_url, "url")}
                data-testid="referral-share-url-copy"
                className="ml-auto shrink-0 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-2.5 py-1.5 text-xs font-semibold transition-colors"
              >
                {copiedField === "url" ? <Check size={12} /> : <Copy size={12} />}
                {copiedField === "url" ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <Stat icon={<Users size={14} />} label="Signed up" value={data?.referrals_signed_up || 0} testid="referral-stat-signed-up" />
          <Stat icon={<Award size={14} />} label="Verified" value={data?.referrals_verified || 0} testid="referral-stat-verified" />
          <Stat icon={<Gift size={14} />} label="Available" value={gbp(availableCredit)} testid="referral-stat-credit" />
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="bg-white/5 border border-white/15 p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60 inline-flex items-center gap-1">{icon} {label}</p>
      <p className="mt-1 text-lg font-bold tracking-tight">{value}</p>
    </div>
  );
}
